import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Mail, 
  Video, 
  FileText, 
  BookMarked, 
  Calendar, 
  Heart, 
  ChevronRight,
  Clock,
  Edit,
  Star,
  Image,
  AlertCircle,
  Plus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { useNavigate } from 'react-router-dom';

interface JournalEntry {
  id: string;
  title: string;
  content: string;
  date: string;
  mood?: string;
  tags?: string[];
  images?: string[];
}

interface Letter {
  id: string;
  recipient: string;
  subject: string;
  content: string;
  date: string;
  delivery_date?: string;
  status: 'draft' | 'scheduled' | 'delivered';
}

interface VideoMessage {
  id: string;
  title: string;
  description?: string;
  date: string;
  url: string;
  thumbnail?: string;
  recipients?: string[];
  duration: string;
}

interface Note {
  id: string;
  title: string;
  content: string;
  date: string;
  category: string;
  tags?: string[];
  is_private: boolean;
  is_important: boolean;
}

interface MemoryBookChapter {
  id: string;
  memory_book_id: string;
  title: string;
  content?: string;
  chapter_order: number;
  images?: string[];
  created_at: string;
  updated_at: string;
}

interface MemoryBook {
  id: string;
  title: string;
  description?: string;
  cover_image?: string;
  is_published: boolean;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

interface LegacyStats {
  totalJournalEntries: number;
  totalWords: number;
  totalImages: number;
  totalLetters: number;
  lettersByStatus: { draft: number; scheduled: number; delivered: number };
  totalVideos: number;
  totalVideoDuration: string;
  totalNotes: number;
  totalMemoryBookChapters: number;
  totalMemoryBookPages: number;
  recentActivity: Array<{
    type: 'journal' | 'letter' | 'video' | 'note' | 'memory';
    title: string;
    date: string;
    id: string;
  }>;
}

export function LegacyDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentJournalEntries, setRecentJournalEntries] = useState<JournalEntry[]>([]);
  const [recentLetters, setRecentLetters] = useState<Letter[]>([]);
  const [recentVideos, setRecentVideos] = useState<VideoMessage[]>([]);
  const [recentNotes, setRecentNotes] = useState<Note[]>([]);
  const [stats, setStats] = useState<LegacyStats>({
    totalJournalEntries: 0,
    totalWords: 0,
    totalImages: 0,
    totalLetters: 0,
    lettersByStatus: { draft: 0, scheduled: 0, delivered: 0 },
    totalVideos: 0,
    totalVideoDuration: '0 minutes',
    totalNotes: 0,
    totalMemoryBookChapters: 0,
    totalMemoryBookPages: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Load all data in parallel
        const [
          journalResponse,
          lettersResponse,
          videosResponse,
          notesResponse,
          memoryBooksResponse,
          chaptersResponse
        ] = await Promise.all([
          supabase
            .from('legacy_journal_entries')
            .select('*')
            .eq('user_id', user.id)
            .order('date', { ascending: false }),
          supabase
            .from('legacy_letters')
            .select('*')
            .eq('user_id', user.id)
            .order('date', { ascending: false }),
          supabase
            .from('legacy_videos')
            .select('*')
            .eq('user_id', user.id)
            .order('date', { ascending: false }),
          supabase
            .from('legacy_notes')
            .select('*')
            .eq('user_id', user.id)
            .order('date', { ascending: false }),
          supabase
            .from('legacy_memory_book')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('legacy_memory_book_chapters')
            .select('*')
            .order('chapter_order', { ascending: true })
        ]);

        // Check for errors
        if (journalResponse.error) throw journalResponse.error;
        if (lettersResponse.error) throw lettersResponse.error;
        if (videosResponse.error) throw videosResponse.error;
        if (notesResponse.error) throw notesResponse.error;
        if (memoryBooksResponse.error) throw memoryBooksResponse.error;
        if (chaptersResponse.error) throw chaptersResponse.error;

        const journalEntries = journalResponse.data || [];
        const letters = lettersResponse.data || [];
        const videos = videosResponse.data || [];
        const notes = notesResponse.data || [];
        const memoryBooks = memoryBooksResponse.data || [];
        const chapters = chaptersResponse.data || [];

        // Calculate statistics
        const totalWords = journalEntries.reduce((count, entry) => 
          count + (entry.content?.split(' ').length || 0), 0
        );

        const totalImages = journalEntries.reduce((count, entry) => 
          count + (entry.images?.length || 0), 0
        );

        // Calculate average words per entry
        const avgWordsPerEntry = journalEntries.length > 0 ? Math.round(totalWords / journalEntries.length) : 0;

        // Calculate total unique tags
        const allTags = journalEntries.flatMap(entry => entry.tags || []);
        const uniqueTags = new Set(allTags);
        const totalTags = uniqueTags.size;

        const lettersByStatus = {
          draft: letters.filter(l => l.status === 'draft').length,
          scheduled: letters.filter(l => l.status === 'scheduled').length,
          delivered: letters.filter(l => l.status === 'delivered').length
        };

        // Calculate total video duration and recipients
        const totalVideoDuration = videos.reduce((total, video) => {
          const duration = video.duration || '0';
          // Handle different duration formats: "15", "15 minutes", "15 min", etc.
          const durationStr = duration.toString().toLowerCase();
          let minutes = 0;
          
          if (durationStr.includes('minute') || durationStr.includes('min')) {
            // Extract number from "15 minutes" or "15 min"
            const match = durationStr.match(/(\d+)/);
            minutes = match ? parseInt(match[1]) : 0;
          } else {
            // Assume it's just a number
            minutes = parseInt(duration) || 0;
          }
          
          return total + minutes;
        }, 0);

        // Calculate total unique recipients across all videos
        const allVideoRecipients = videos.flatMap(video => video.recipients || []);
        const uniqueRecipients = new Set(allVideoRecipients);
        const totalRecipients = uniqueRecipients.size;

        // Format duration nicely
        const formatDuration = (totalMinutes: number) => {
          if (totalMinutes === 0) return '0 minutes';
          if (totalMinutes < 60) return `${totalMinutes} minutes`;
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
          return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
        };

        // Calculate notes statistics
        const importantNotes = notes.filter(note => note.is_important).length;
        const privateNotes = notes.filter(note => note.is_private).length;

        // Create recent activity list
        const recentActivity = [
          ...journalEntries.slice(0, 3).map(entry => ({
            type: 'journal' as const,
            title: entry.title,
            date: entry.date,
            id: entry.id
          })),
          ...letters.slice(0, 2).map(letter => ({
            type: 'letter' as const,
            title: letter.subject,
            date: letter.date,
            id: letter.id
          })),
          ...videos.slice(0, 2).map(video => ({
            type: 'video' as const,
            title: video.title,
            date: video.date,
            id: video.id
          })),
          ...notes.slice(0, 2).map(note => ({
            type: 'note' as const,
            title: note.title,
            date: note.date,
            id: note.id
          })),
          ...memoryBooks.slice(0, 2).map(book => ({
            type: 'memory' as const,
            title: book.title,
            date: book.created_at,
            id: book.id
          }))
        ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
         .slice(0, 5);

        setStats({
          totalJournalEntries: journalEntries.length,
          totalWords,
          totalImages,
          totalLetters: letters.length,
          lettersByStatus,
          totalVideos: videos.length,
          totalVideoDuration: formatDuration(totalVideoDuration),
          totalNotes: notes.length,
          totalMemoryBookChapters: chapters.length,
          totalMemoryBookPages: memoryBooks.length,
          recentActivity
        });

        setRecentJournalEntries(journalEntries.slice(0, 3));
        setRecentLetters(letters.slice(0, 2));
        setRecentVideos(videos.slice(0, 2));
        setRecentNotes(notes.slice(0, 2));

      } catch (err) {
        console.error('Error loading legacy data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load legacy data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'journal': return <BookOpen size={16} className="text-amber-600" />;
      case 'letter': return <Mail size={16} className="text-blue-600" />;
      case 'video': return <Video size={16} className="text-purple-600" />;
      case 'note': return <FileText size={16} className="text-green-600" />;
      case 'memory': return <BookMarked size={16} className="text-rose-600" />;
      default: return <Calendar size={16} className="text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
        <div className="h-64 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-amber-50 to-rose-50 rounded-xl p-6 border border-amber-100">
        <h1 className="text-2xl font-semibold mb-2">Legacy Dashboard</h1>
        <p className="text-gray-600">
          Preserve your memories, wisdom, and messages for your loved ones
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      {/* Legacy Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-all"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-50 rounded-lg">
              <BookOpen className="text-amber-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Journal</h3>
              <p className="text-sm text-gray-500">{stats.totalJournalEntries} entries recorded</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Latest Entry:</span>
              <span className="font-medium">
                {recentJournalEntries.length > 0 ? formatDate(recentJournalEntries[0].date) : 'None'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Words:</span>
              <span className="font-medium">{stats.totalWords.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Avg Words/Entry:</span>
              <span className="font-medium">{avgWordsPerEntry}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Photos:</span>
              <span className="font-medium">{stats.totalImages}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Tags:</span>
              <span className="font-medium">{totalTags}</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => navigate('/dashboard/legacy/journal')}
          >
            View Journal
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-all"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Mail className="text-blue-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Letters</h3>
              <p className="text-sm text-gray-500">{stats.totalLetters} letters written</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Drafts:</span>
              <span className="font-medium">{stats.lettersByStatus.draft}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Scheduled:</span>
              <span className="font-medium">{stats.lettersByStatus.scheduled}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Delivered:</span>
              <span className="font-medium">{stats.lettersByStatus.delivered}</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => navigate('/dashboard/legacy/letters')}
          >
            View Letters
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-all"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Video className="text-purple-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Video Messages</h3>
              <p className="text-sm text-gray-500">{stats.totalVideos} videos recorded</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Duration:</span>
              <span className="font-medium">{stats.totalVideoDuration}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Latest Recording:</span>
              <span className="font-medium">
                {recentVideos.length > 0 ? formatDate(recentVideos[0].date) : 'None'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Recipients:</span>
              <span className="font-medium">
                {totalRecipients > 0 ? totalRecipients : 'None'}
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => navigate('/dashboard/legacy/videos')}
          >
            View Videos
          </Button>
        </motion.div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-all"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <FileText className="text-green-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Notes</h3>
              <p className="text-sm text-gray-500">{stats.totalNotes} notes created</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Important Notes:</span>
              <span className="font-medium">
                {notes.filter(note => note.is_important).length}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Private Notes:</span>
              <span className="font-medium">
                {notes.filter(note => note.is_private).length}
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => navigate('/dashboard/legacy/notes')}
          >
            View Notes
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-all"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-rose-50 rounded-lg">
              <BookMarked className="text-rose-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Memory Books</h3>
              <p className="text-sm text-gray-500">{stats.totalMemoryBookPages} books created</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Books:</span>
              <span className="font-medium">{stats.totalMemoryBookPages}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Chapters:</span>
              <span className="font-medium">{stats.totalMemoryBookChapters}</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => navigate('/dashboard/legacy/memory-book')}
          >
            View Memory Books
          </Button>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <Card>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Recent Activity</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/dashboard/legacy')}
            >
              View All
            </Button>
          </div>
          
          <div className="space-y-3">
            {stats.recentActivity.length > 0 ? (
              stats.recentActivity.map((activity, index) => (
                <div key={`${activity.type}-${activity.id}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  {getActivityIcon(activity.type)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{activity.title}</p>
                    <p className="text-xs text-gray-500 capitalize">{activity.type} • {formatDate(activity.date)}</p>
                  </div>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-sm text-gray-500">
                No recent activity
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/dashboard/legacy/journal')}
              icon={<Plus size={20} />}
            >
              Add Journal Entry
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/dashboard/legacy/letters')}
              icon={<Plus size={20} />}
            >
              Write Letter
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate('/dashboard/legacy/videos')}
              icon={<Plus size={20} />}
            >
              Record Video
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}